import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth as useClerkAuth } from '@clerk/react'

import { Button } from '@/components/ui/button'
import { AXIOS_INSTANCE } from '@/features/auth/axios-config'
import {
  CLERK_JWT_TEMPLATE,
  CLERK_TOKEN_FETCH_CONTEXT_KEY,
} from '@/features/auth/constants'
import ServerError from '@/public/images/server-error.svg'
import { ROUTES } from '@/utils/routes'

interface ClerkTokenFetchContext {
  type: 'clerk-token-fetch'
  errorStatus: number | null
  sourceUrl: string
  timestamp: number
}

const InternalServerError = () => {
  const navigate = useNavigate()
  const [isRetrying, setIsRetrying] = useState(false)
  const { getToken, signOut } = useClerkAuth()

  const handleClerkTokenRetry = useCallback(async () => {
    const clerkContextStr = sessionStorage.getItem(
      CLERK_TOKEN_FETCH_CONTEXT_KEY
    )
    if (!clerkContextStr) return false

    const clerkContext: ClerkTokenFetchContext = JSON.parse(clerkContextStr)
    const sourceUrl = clerkContext.sourceUrl || ROUTES.ROOT

    try {
      const newToken = await getToken({ template: CLERK_JWT_TEMPLATE })

      if (newToken) {
        localStorage.setItem('token', newToken)
        sessionStorage.removeItem(CLERK_TOKEN_FETCH_CONTEXT_KEY)
        navigate(sourceUrl, { replace: true })
        return true
      }

      throw new Error('No token returned')
    } catch {
      sessionStorage.removeItem(CLERK_TOKEN_FETCH_CONTEXT_KEY)
      localStorage.removeItem('token')

      try {
        if (signOut) {
          await signOut()
        }
      } catch {
        // Ignore signOut errors
      }

      globalThis.location.href = ROUTES.LOGIN
      return true
    }
  }, [getToken, signOut, navigate])

  const handleTryAgain = useCallback(async () => {
    setIsRetrying(true)

    // Check if this is a Clerk token fetch failure
    const clerkContextStr = sessionStorage.getItem(
      CLERK_TOKEN_FETCH_CONTEXT_KEY
    )
    if (clerkContextStr) {
      await handleClerkTokenRetry()
      setIsRetrying(false)
      return
    }

    // Handle regular axios request failures
    const failedRequestContext = sessionStorage.getItem('failedRequestContext')

    if (!failedRequestContext) {
      navigate(ROUTES.ROOT, { replace: true })
      return
    }

    const requestConfig = JSON.parse(failedRequestContext)
    const sourceUrl = requestConfig.sourceUrl || ROUTES.ROOT

    try {
      await AXIOS_INSTANCE.request({
        method: requestConfig.method,
        url: requestConfig.url,
        data: requestConfig.data,
        params: requestConfig.params,
      })

      sessionStorage.removeItem('failedRequestContext')

      // Success → navigate back to source page
      navigate(sourceUrl, { replace: true })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Retry failed:', error)

      sessionStorage.removeItem('failedRequestContext')

      // Retry failed → still navigate back to source page
      navigate(sourceUrl, { replace: true })
    } finally {
      setIsRetrying(false)
    }
  }, [navigate, handleClerkTokenRetry])

  return (
    <main className="flex items-center justify-center min-h-screen px-4 py-16 bg-slate-50">
      <section className="w-full max-w-xl p-10 text-center bg-white shadow-lg rounded-2xl">
        <img
          src={ServerError}
          alt="Internal Server Error"
          className="mx-auto h-[240px] w-[240px]"
        />
        <h2 className="p-0 m-0 mt-2 font-bold text-gray-900 text-md">
          Something Went Wrong
        </h2>
        <p className="m-0 font-normal text-md text-tertiary-600">
          An unexpected error occurred on our side. <br /> Please try again
          later or contact support if the problem persists.
        </p>
        <div className="flex flex-col gap-3 mt-2 sm:flex-row sm:justify-center">
          <Button
            onClick={handleTryAgain}
            variant="solid"
            color="primary"
            className="px-4 py-3 text-sm rounded-full"
            disabled={isRetrying}
          >
            {isRetrying ? 'Retrying...' : 'Try again'}
          </Button>
        </div>
      </section>
    </main>
  )
}

export default InternalServerError
